/**
 * Care Concierge session credit API (Feature 106).
 *
 * Both pools — a firm's shared sessions and an individual advisor's — are served by the same
 * endpoints, with ownership in the request rather than the route.
 */

import { apiClient } from '@/core/api/client';

const BASE_PATH = '/admin/care-concierge';

/**
 * USER rather than ADVISOR: credit belongs to an account, so a non-advisor could hold sessions
 * later without a second set of columns.
 */
export type CreditOwnerType = 'FIRM' | 'USER';

export type SessionGrantRow = {
  id: string;
  /** Positive on a grant, negative on a reversal. */
  sessions: number;
  note: string | null;
  grantedByName: string;
  createdAt: string;
  /** Set on a reversal row: the grant it voids. */
  reversalOfGrantId: string | null;
  /** True when this grant has already been voided. */
  isReversed: boolean;
};

export type SessionCreditBalance = {
  granted: number;
  /** Live sponsored cases drawing on the pool. Derived, never stored. */
  used: number;
  remaining: number;
};

export type SessionCredit = {
  ownerType: CreditOwnerType;
  ownerId: string;
  balance: SessionCreditBalance;
  grants: SessionGrantRow[];
};

export async function getSessionCredit(
  ownerType: CreditOwnerType,
  ownerId: string,
): Promise<SessionCredit> {
  const response = await apiClient.get<SessionCredit>(
    `${BASE_PATH}/credit/${ownerType}/${ownerId}`,
  );
  return response.data;
}

export async function grantSessions(payload: {
  ownerType: CreditOwnerType;
  ownerId: string;
  sessions: number;
  note?: string;
}): Promise<SessionGrantRow> {
  const response = await apiClient.post<SessionGrantRow>(`${BASE_PATH}/grants`, payload);
  return response.data;
}

/** Appends a negative row; the original grant is never modified. */
export async function reverseSessionGrant(
  grantId: string,
  note?: string,
): Promise<SessionGrantRow> {
  const response = await apiClient.post<SessionGrantRow>(
    `${BASE_PATH}/grants/${grantId}/reversal`,
    note ? { note } : {},
  );
  return response.data;
}

/**
 * The Care Concierge on/off switch. Two explicit shapes rather than one with an owner
 * discriminator, because the ids are different things: a partner company id and a financial
 * advisor id — and the credit endpoints above take a *user* id for the individual pool. One
 * discriminator across all three would invite passing the wrong one.
 */
export type CareConciergeEnabled = { enabled: boolean };

export async function getPartnerCompanyConciergeEnabled(
  partnerCompanyId: string,
): Promise<CareConciergeEnabled> {
  const response = await apiClient.get<CareConciergeEnabled>(
    `${BASE_PATH}/enabled/partner-companies/${partnerCompanyId}`,
  );
  return response.data;
}

export async function setPartnerCompanyConciergeEnabled(
  partnerCompanyId: string,
  enabled: boolean,
): Promise<CareConciergeEnabled> {
  const response = await apiClient.patch<CareConciergeEnabled>(
    `${BASE_PATH}/enabled/partner-companies/${partnerCompanyId}`,
    { enabled },
  );
  return response.data;
}

export async function getAdvisorConciergeEnabled(
  advisorId: string,
): Promise<CareConciergeEnabled> {
  const response = await apiClient.get<CareConciergeEnabled>(
    `${BASE_PATH}/enabled/advisors/${advisorId}`,
  );
  return response.data;
}

export async function setAdvisorConciergeEnabled(
  advisorId: string,
  enabled: boolean,
): Promise<CareConciergeEnabled> {
  const response = await apiClient.patch<CareConciergeEnabled>(
    `${BASE_PATH}/enabled/advisors/${advisorId}`,
    { enabled },
  );
  return response.data;
}

/**
 * The self-pay consultation fee, same two shapes as the switch. `selfPayFeeCents` is what is
 * stored at this level (null = inherit); the server also says what that resolves to.
 */
export type CareConciergeFee = {
  selfPayFeeCents: number | null;
  /** The firm fee for an advisor, the platform default for a firm. */
  inheritedFeeCents: number;
  effectiveFeeCents: number;
  /** effectiveFeeCents formatted, e.g. "$350". */
  effectiveFee: string;
  /** The platform default — also what the self-pay Calendly event type advertises. */
  platformDefaultFeeCents: number;
};

export async function getPartnerCompanyConciergeFee(
  partnerCompanyId: string,
): Promise<CareConciergeFee> {
  const response = await apiClient.get<CareConciergeFee>(
    `${BASE_PATH}/fee/partner-companies/${partnerCompanyId}`,
  );
  return response.data;
}

export async function setPartnerCompanyConciergeFee(
  partnerCompanyId: string,
  selfPayFeeCents: number | null,
): Promise<CareConciergeFee> {
  const response = await apiClient.patch<CareConciergeFee>(
    `${BASE_PATH}/fee/partner-companies/${partnerCompanyId}`,
    { selfPayFeeCents },
  );
  return response.data;
}

export async function getAdvisorConciergeFee(advisorId: string): Promise<CareConciergeFee> {
  const response = await apiClient.get<CareConciergeFee>(`${BASE_PATH}/fee/advisors/${advisorId}`);
  return response.data;
}

export async function setAdvisorConciergeFee(
  advisorId: string,
  selfPayFeeCents: number | null,
): Promise<CareConciergeFee> {
  const response = await apiClient.patch<CareConciergeFee>(
    `${BASE_PATH}/fee/advisors/${advisorId}`,
    { selfPayFeeCents },
  );
  return response.data;
}
